import { Permiso } from "../../shared/model/permisos/permiso.model";
import { notaVisible, Vacacion } from "../../shared/model/vacaciones/vacacion.model";

/**
 * La pestaña «Autorizaciones» del calendario de vacaciones (13/10/2026).
 *
 * Norman: «en lugar de que salgan así, mejor otra pestaña que se llame
 * Autorizaciones y ahí salgan las de vacaciones y permisos para que se
 * autoricen … mejor controlado, bien visible y fácil de usar». Lo que
 * espera respuesta ya no va en una banda encima de todas las pestañas ni
 * en otra dentro de Permisos: va aquí, en una sola tabla.
 *
 * Una solicitud de vacaciones y un permiso son cosas distintas (cada uno
 * con su servicio, su documento y su manera de resolverse), pero quien
 * autoriza los lee igual: quién, qué pide y cuándo. Este archivo los pone
 * en el mismo renglón.
 */

export type TipoAutorizacion = "vacaciones" | "permisos";

/** Qué se ve en la tabla: las dos cosas o una sola. */
export type FiltroAutorizacion = "todas" | TipoAutorizacion;

/** Un renglón de la tabla: una solicitud de vacaciones o un permiso. */
export interface FilaAutorizacion {
  /** Para el trackBy: el tipo y su id («v12», «p7»). */
  clave: string;
  tipo: TipoAutorizacion;
  /** El original, para sus botones: uno de los dos. */
  vacacion: Vacacion | null;
  permiso: Permiso | null;
  colaborador: string;
  /** Por qué le llegó a quien la ve, si resuelve en lugar de otro. */
  suplencia: string;
  /** «Vacaciones», «Faltar el día», «Salir a las 14:30»… con su icono. */
  que: string;
  icono: string;
  clase: string;
  /** Lo que escribió quien pide. */
  nota: string;
  /** «15 al 16 de octubre», y debajo, si hay, «Regresa el 19 de octubre». */
  cuando: string;
  detalle: string;
  /** Días hábiles; null en un permiso, que es de un solo día. */
  dias: number | null;
  /** El primer día, en ISO: la tabla va de lo más próximo a lo más lejano. */
  fecha: string;
}

const MESES = [
  "enero", "febrero", "marzo", "abril", "mayo", "junio",
  "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre",
];

/** Cómo se dice cada permiso en «Qué pide». */
const PERMISOS: Record<string, { icono: string; clase: string; que: (hora: string) => string }> = {
  FALTA: { icono: "event_busy", clase: "es-falta", que: () => "Faltar el día" },
  SALIDA: { icono: "logout", clase: "es-salida", que: (hora) => `Salir a las ${hora}`.trim() },
  ENTRADA: { icono: "login", clase: "es-entrada", que: (hora) => `Llegar a las ${hora}`.trim() },
};

/** Año, mes (0 a 11) y día de una fecha ISO, o null si no lo es. */
function partes(iso?: string | null): { anio: number; mes: number; dia: number } | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso || "");
  return m ? { anio: Number(m[1]), mes: Number(m[2]) - 1, dia: Number(m[3]) } : null;
}

/** «15 de octubre», y «15 de enero de 2027» cuando no es de este año. */
export function diaLargo(iso: string | undefined | null, anioActual: number): string {
  const f = partes(iso);
  if (!f) {
    return "";
  }
  const anio = f.anio === anioActual ? "" : ` de ${f.anio}`;
  return `${f.dia} de ${MESES[f.mes]}${anio}`;
}

/**
 * «15 al 16 de octubre», «30 de octubre al 3 de noviembre» o, si es un
 * solo día, «15 de octubre». El mes va una vez cuando es el mismo.
 */
export function rangoLargo(desde: string | undefined, hasta: string | undefined, anioActual: number): string {
  const a = partes(desde);
  const b = partes(hasta);
  if (!a || !b || desde === hasta) {
    return diaLargo(desde || hasta, anioActual);
  }
  if (a.anio === b.anio && a.mes === b.mes) {
    return `${a.dia} al ${diaLargo(hasta, anioActual)}`;
  }
  // De un año a otro, cada punta dice el suyo.
  const anioIni = a.anio === b.anio ? a.anio : -1;
  return `${diaLargo(desde, anioIni)} al ${diaLargo(hasta, anioActual)}`;
}

/** Quien resuelve en lugar de otro lo ve dicho: «Irving está de vacaciones». */
function suplenciaDe(x: { suplenteDe?: string; ausencia?: string }): string {
  return x.suplenteDe && x.ausencia ? `${x.suplenteDe} está ${x.ausencia}` : "";
}

/** Cuándo: el rango o, con días sueltos (15/10/2026), cada tramo: «2 al 4 de noviembre y 23 de noviembre». */
function cuandoDe(v: Vacacion, anioActual: number): string {
  if (v.tramos && v.tramos.length > 1) {
    const textos = v.tramos.map((t) => rangoLargo(t.fechaInicio, t.fechaFin, anioActual));
    return `${textos.slice(0, -1).join(", ")} y ${textos.at(-1)}`;
  }
  return rangoLargo(v.fechaInicio, v.fechaFin, anioActual);
}

/** Debajo del cuándo: en cuántos tramos va, o el día que regresa. */
function detalleDe(v: Vacacion, anioActual: number): string {
  if (v.tramos && v.tramos.length > 1) {
    return `En ${v.tramos.length} tramos, una sola solicitud`;
  }
  return v.fechaRegreso ? `Regresa el ${diaLargo(v.fechaRegreso, anioActual)}` : "";
}

function filaDeVacacion(v: Vacacion, dias: (v: Vacacion) => number, anioActual: number): FilaAutorizacion {
  return {
    clave: `v${v.idVacacion}`,
    tipo: "vacaciones",
    vacacion: v,
    permiso: null,
    colaborador: v.colaborador || "",
    suplencia: suplenciaDe(v),
    que: "Vacaciones",
    icono: "beach_access",
    clase: "es-vacaciones",
    nota: notaVisible(v.comentario),
    cuando: cuandoDe(v, anioActual),
    detalle: detalleDe(v, anioActual),
    dias: dias(v),
    fecha: v.fechaInicio || "",
  };
}

function filaDePermiso(p: Permiso, anioActual: number): FilaAutorizacion {
  const tipo = PERMISOS[p.tipo || ""] || PERMISOS.FALTA;
  return {
    clave: `p${p.idPermiso}`,
    tipo: "permisos",
    vacacion: null,
    permiso: p,
    colaborador: p.colaborador || "",
    suplencia: suplenciaDe(p),
    que: tipo.que(p.hora || ""),
    icono: tipo.icono,
    clase: tipo.clase,
    nota: (p.observaciones || "").trim(),
    cuando: diaLargo(p.fecha, anioActual),
    detalle: "",
    dias: null,
    fecha: (p.fecha || "").substring(0, 10),
  };
}

/**
 * Los renglones de la tabla: las vacaciones y los permisos que esperan
 * respuesta, de la fecha más próxima a la más lejana (lo que urge,
 * arriba). `dias` cuenta los días hábiles de una solicitud de vacaciones:
 * lo sabe el calendario, que conoce los festivos.
 */
export function filasDeAutorizacion(
  vacaciones: Vacacion[],
  permisos: Permiso[],
  dias: (v: Vacacion) => number,
  anioActual: number,
): FilaAutorizacion[] {
  return [
    ...vacaciones.map((v) => filaDeVacacion(v, dias, anioActual)),
    ...permisos.map((p) => filaDePermiso(p, anioActual)),
  ].sort((a, b) => a.fecha.localeCompare(b.fecha) || a.colaborador.localeCompare(b.colaborador, "es"));
}

/** Las de un tipo, o todas. */
export function filtraAutorizaciones(filas: FilaAutorizacion[], filtro: FiltroAutorizacion): FilaAutorizacion[] {
  return filtro === "todas" ? filas : filas.filter((f) => f.tipo === filtro);
}
